import { useEffect, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { consultationsApi, type ConsultationReview } from '@/api/consultationsApi';
import { cn } from '@/lib/utils';

const FIRST_PAGE = 5;
const NEXT_PAGE = 10;

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn('size-3.5', n <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30')}
        />
      ))}
    </span>
  );
}

/**
 * Reviews of online consultations, from the API rather than the page, so a
 * new review appears without a deploy. Five first, then ten at a time — the
 * same rhythm as clinic reviews.
 */
export function ConsultationReviews() {
  const [items, setItems] = useState<ConsultationReview[] | null>(null);
  const [summary, setSummary] = useState<{ average: number | null; count: number }>({ average: null, count: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    consultationsApi
      .reviews({ page: 1, limit: FIRST_PAGE })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data.reviews);
        setSummary({ average: res.data.average, count: res.data.count });
        setHasMore(res.data.has_more);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      // The API pages by a fixed size. Work out which ten-wide page holds the
      // next unseen review and drop the part of it we already have, so the
      // first "Show more" after five yields the five that follow, never a gap.
      const loaded = items?.length ?? 0;
      const nextPage = Math.floor(loaded / NEXT_PAGE) + 1;
      const res = await consultationsApi.reviews({ page: nextPage, limit: NEXT_PAGE });
      const fresh = res.data.reviews.slice(loaded - (nextPage - 1) * NEXT_PAGE);
      setItems((prev) => [...(prev ?? []), ...fresh]);
      setHasMore(res.data.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  if (items === null) {
    return (
      <div className="mt-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="mt-4 text-muted-foreground">No reviews yet.</p>;
  }

  return (
    <div className="mt-4">
      {summary.average != null && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Stars rating={summary.average} />
          <span className="font-semibold text-foreground">{summary.average.toFixed(1)}</span>
          <span>· {summary.count} {summary.count === 1 ? 'review' : 'reviews'}</span>
        </p>
      )}
      <ul className="mt-4 divide-y">
        {items.map((r, i) => (
          <li key={`${r.name}-${r.date}-${i}`} className="py-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium text-foreground">{r.name}</span>
              <Stars rating={r.rating} />
              {r.date && <span className="text-xs text-muted-foreground">{r.date}</span>}
            </div>
            {r.text && <p className="mt-1.5 max-w-2xl leading-relaxed text-muted-foreground">{r.text}</p>}
          </li>
        ))}
      </ul>
      {hasMore && (
        <Button variant="outline" size="sm" className="mt-2" onClick={loadMore} disabled={loadingMore}>
          {loadingMore && <Loader2 className="size-4 animate-spin" />}
          Show more
        </Button>
      )}
    </div>
  );
}
